import React, {useCallback, useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps, useEffectEvent, useEffectItem} from 'helpers/hooks/utils';
import {useTable} from 'components/organisms/Providers/TableProvider/TableProvider';
import DealflowRundownBarChart from 'components/organisms/Charts/DealflowRundownBarChart/DealflowRundownBarChart';
import StyledDealflowRundownGraphCard
  from 'components/organisms/Cards/DealflowRundownGraphCard/DealflowRundownGraphCard.styles';
import constants from 'helpers/constants';
import InlineForm from 'components/organisms/Forms/InlineForm/InlineForm';
import {useClientStatisticDealflowRundown} from 'services/client/statistic/statistic.utils';
import Box from 'components/atoms/Layout/Box/Box';
import {P} from 'components/atoms/Text/Typography/Typography';
import {useLinkNavigate} from 'helpers/hooks/links';
import utils from 'helpers/utils';
import SimpleLoader from 'components/molecules/Loaders/SimpleLoader/SimpleLoader';

const DealflowRundownGraphCard = React.forwardRef((props, ref) => {
  const innerProps = useComponentProps(props, 'DealflowRundownGraphCard');

  const tableProvider = useTable();
  const graphState = tableProvider.graphState;

  const [dealflow, setDealflow] = useState(null);

  const navigate = useLinkNavigate();

  const listState = useEffectItem(tableProvider.appliedListState());
  const year = graphState.graphSettings['rundown']?.filter?.year;
  const months = graphState.graphSettings['rundown']?.filter?.months ?? 12;

  const [dealflowRundown, isLoading] = useClientStatisticDealflowRundown(
    year,
    months,
    listState.search,
    listState.filter
  );

  useEffect(() => {
    if (dealflowRundown) {
      setDealflow(dealflowRundown);
    }
  }, [dealflowRundown]);

  const fields = useMemo(() => {
    const initialOption = constants.data.periodMonths.find((opt) => opt.value === months);

    return [{
      name: 'months',
      type: constants.formFieldTypes.autocomplete,
      validation: constants.formFieldValidationTypes.text,
      placeholder: 'Select a period',
      initial: initialOption,
      FormFieldProps: {
        autoFocus: false,
        hiddenLabel: true,
        clearable: false,
        size: 'small'
      },
      options: constants.data.periodMonths
    }];
  }, [months]);

  const setGraphSettingsEvent = useEffectEvent(graphState.setGraphSettings);
  const handleFilterChange = useCallback((field, value) => {
    setGraphSettingsEvent?.((current) => ({
      ...current,
      'rundown': {
        ...current?.['rundown'],
        filter: {
          ...current?.['rundown']?.filter,
          months: value?.value ?? value
        }
      }
    }));
  }, [setGraphSettingsEvent]);

  const handleVisibilityChange = (visibility) => {
    graphState.setGraphSettings((current) => ({
      ...current,
      'rundown': {
        ...current?.['rundown'],
        visibility
      }
    }));
  };

  const handleClick = (e, release, data) => {
    const group = data.tooltipPayload?.[0]?.dataKey;
    if (group && months) {
      navigate({
        event: e,
        to: `/dealflow/table?custom=dealflowRundown:${group}_${months}`,
        keepSearchParams: true
      });
    }
  };

  return <StyledDealflowRundownGraphCard ref={ref} {...innerProps}
                                         title="Deal flow rundown"
                                         context={(fields ? <InlineForm onChange={handleFilterChange}
                                                                        fields={fields} /> : null)}>
    {(utils.isDefined(dealflow) && dealflow?.length > 0) ?
      <DealflowRundownBarChart dealflow={dealflow}
                               showLegend={true}
                               isLoading={isLoading}
                               onClick={handleClick}
                               visibility={graphState.graphSettings['rundown']?.visibility}
                               onVisibilityChange={handleVisibilityChange} /> : null}

    {(!utils.isDefined(dealflow) || dealflow?.length === 0) ? (
      (!utils.isDefined(dealflow) || isLoading) ?
        <SimpleLoader className="GraphCard-loader" /> :
        <Box className="GraphCard-empty">
          <P>No deal flow data found</P>
        </Box>
    ) : null}
  </StyledDealflowRundownGraphCard>
});

DealflowRundownGraphCard.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ])
};

export default DealflowRundownGraphCard;
